import { CommentsoldManagedReturnsSchema } from "@redotech/redo-model/fulfillments/commentsold-managed-returns";
import { ShopifyManagedReturnsSchema } from "@redotech/redo-model/fulfillments/shopify-managed-returns";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

export const managedReturnsIntegrationProvider = z.enum([
  "shopify",
  "commentsold",
]);
export type ManagedReturnsIntegrationProvider = z.infer<
  typeof managedReturnsIntegrationProvider
>;

const managedReturnsIntegrationSummarySchema = z.object({
  provider: managedReturnsIntegrationProvider,
  name: z.string(),
  enabled: z.boolean(),
});
export type ManagedReturnsIntegrationSummary = z.infer<
  typeof managedReturnsIntegrationSummarySchema
>;

/**
 * Per-provider managed-returns settings, discriminated on `provider` so each
 * integration keeps its own shape (shared rule pipeline lives inside each).
 */
export const managedReturnsIntegrationSettings = z.discriminatedUnion(
  "provider",
  [
    z.object({
      provider: z.literal("shopify"),
      settings: ShopifyManagedReturnsSchema,
    }),
    z.object({
      provider: z.literal("commentsold"),
      settings: CommentsoldManagedReturnsSchema,
    }),
  ],
);
export type ManagedReturnsIntegrationSettings = z.infer<
  typeof managedReturnsIntegrationSettings
>;

export const getManagedReturnsIntegrations = unaryRpc({
  name: "getManagedReturnsIntegrations" as const,
  input: z.object({}),
  output: z.object({
    integrations: z.array(managedReturnsIntegrationSummarySchema),
  }),
});

export const getManagedReturnsIntegrationSettings = unaryRpc({
  name: "getManagedReturnsIntegrationSettings" as const,
  input: z.object({ provider: managedReturnsIntegrationProvider }),
  output: managedReturnsIntegrationSettings,
});

export const updateManagedReturnsIntegrationSettings = unaryRpc({
  name: "updateManagedReturnsIntegrationSettings" as const,
  input: managedReturnsIntegrationSettings,
  output: z.object({ ok: z.literal(true) }),
});
